/**
 * Dashboard — the operator's home screen.
 *
 * Goals (in order):
 *   1. Surface what needs attention right now (failures, missing connections).
 *   2. Show what's running so they don't kick off duplicate work.
 *   3. Give a per-project health snapshot for quick triage.
 *   4. Stream recent completed activity for context.
 *
 * Anti-goals: vanity counters (total runs ever), duplicate widgets, and
 * "quick start" links that don't take context into account.
 */
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CircleDot,
  Clock,
  FileSearch,
  FolderKanban,
  Globe,
  KeyRound,
  LineChart,
  Notebook,
  Target,
  TrendingUp,
} from 'lucide-react';
import type { Audit, Campaign, Project } from '@seo/types';
import { Page, PageHeader } from '@/components/ui/page';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { StatTile } from '@/components/ui/stat-tile';
import { Callout } from '@/components/ui/callout';
import { EmptyState } from '@/components/ui/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/status-pill';
import { Spinner } from '@/components/spinner';
import { fadeUp } from '@/components/motion';
import { useAuthStore } from '@/lib/auth';
import { hostname, relTime } from '@/lib/format';
import {
  countByStatus,
  isFailed,
  isInProgress,
  isNeedsReview,
  isSuccess,
} from '@/lib/status';
import { cn } from '@/lib/cn';
import { useProjects } from '@/features/projects/use-projects';
import { useAudits } from '@/features/audits/use-audits';
import { useBriefs } from '@/features/briefs/use-briefs';
import { useKeywordRuns } from '@/features/keywords/use-keyword-runs';
import { useReports } from '@/features/reports/use-reports';
import { useCampaigns } from '@/features/campaigns/use-campaigns';
import { useForecasts } from '@/features/forecasts/use-forecasts';
import { useCompetitorRuns } from '@/features/competitors/use-competitor-runs';

/* ------------------------------------------------------------------ */
/* Activity stream                                                     */
/* ------------------------------------------------------------------ */

type ActivityKind =
  | 'audit'
  | 'brief'
  | 'keyword'
  | 'report'
  | 'campaign'
  | 'forecast'
  | 'competitor';

interface ActivityRow {
  id: string;
  kind: ActivityKind;
  projectId: string;
  label: string;
  status: string;
  createdAt: string;
  to: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
}

const KIND_META: Record<
  ActivityKind,
  { label: string; icon: typeof FileSearch }
> = {
  audit: { label: 'Audit', icon: FileSearch },
  brief: { label: 'Brief', icon: Notebook },
  keyword: { label: 'Keywords', icon: KeyRound },
  report: { label: 'Report', icon: LineChart },
  campaign: { label: 'Campaign', icon: Target },
  forecast: { label: 'Forecast', icon: TrendingUp },
  competitor: { label: 'Competitor scan', icon: Globe },
};

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */

export function DashboardRoute(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const projects = useProjects();
  const audits = useAudits();
  const briefs = useBriefs();
  const keywords = useKeywordRuns();
  const reports = useReports();
  const campaigns = useCampaigns();
  const forecasts = useForecasts();
  const competitorRuns = useCompetitorRuns();

  const projectItems = projects.data?.items ?? [];
  const auditItems = audits.data?.items ?? [];
  const briefItems = briefs.data?.items ?? [];
  const keywordItems = keywords.data?.items ?? [];
  const reportItems = reports.data?.items ?? [];
  const campaignItems = campaigns.data?.items ?? [];
  const forecastItems = forecasts.data?.items ?? [];
  const competitorRunItems = competitorRuns.data?.items ?? [];

  const isLoading =
    projects.isLoading ||
    audits.isLoading ||
    briefs.isLoading ||
    keywords.isLoading ||
    reports.isLoading;

  /* ---- Aggregated counts across every service ---- */
  const totals = useMemo(() => {
    const all = [
      ...auditItems,
      ...briefItems,
      ...keywordItems,
      ...reportItems,
      ...campaignItems,
      ...forecastItems,
      ...competitorRunItems,
    ];
    return countByStatus(all);
  }, [
    auditItems,
    briefItems,
    keywordItems,
    reportItems,
    campaignItems,
    forecastItems,
    competitorRunItems,
  ]);

  const completedLast7 = useMemo(() => {
    const cutoff = Date.now() - 7 * 86400_000;
    const inWindow = (createdAt: string): boolean =>
      new Date(createdAt).getTime() >= cutoff;
    let n = 0;
    for (const a of auditItems)
      if (isSuccess(a.status) && inWindow(a.created_at)) n++;
    for (const b of briefItems)
      if (isSuccess(b.status) && inWindow(b.created_at)) n++;
    for (const r of reportItems)
      if (isSuccess(r.status) && inWindow(r.created_at)) n++;
    return n;
  }, [auditItems, briefItems, reportItems]);

  /* ---- Activity stream (last 8 across all services) ---- */
  const activity = useMemo<ActivityRow[]>(() => {
    const rows: ActivityRow[] = [
      ...auditItems.map<ActivityRow>((a) => ({
        id: `audit-${a.id}`,
        kind: 'audit',
        projectId: a.project_id,
        label:
          a.health_score !== null
            ? `Audit · score ${a.health_score}`
            : `Audit ${a.id.slice(0, 8)}`,
        status: a.status,
        createdAt: a.created_at,
        to: '/audits/$auditId',
        params: { auditId: a.id },
      })),
      ...briefItems.map<ActivityRow>((b) => ({
        id: `brief-${b.id}`,
        kind: 'brief',
        projectId: b.project_id,
        label: b.target_keyword || `Brief ${b.id.slice(0, 8)}`,
        status: b.status,
        createdAt: b.created_at,
        to: '/briefs/$briefId',
        params: { briefId: b.id },
      })),
      ...keywordItems.map<ActivityRow>((k) => ({
        id: `kw-${k.id}`,
        kind: 'keyword',
        projectId: k.project_id,
        label: `Keyword run ${k.id.slice(0, 8)}`,
        status: k.status,
        createdAt: k.created_at,
        to: '/keywords/$keywordRunId',
        params: { keywordRunId: k.id },
      })),
      ...reportItems.map<ActivityRow>((r) => ({
        id: `report-${r.id}`,
        kind: 'report',
        projectId: r.project_id,
        label: `${r.cadence} report · ${r.period_start} → ${r.period_end}`,
        status: r.status,
        createdAt: r.created_at,
        to: '/reports/$reportId',
        params: { reportId: r.id },
      })),
      ...campaignItems.map<ActivityRow>((c) => ({
        id: `camp-${c.id}`,
        kind: 'campaign',
        projectId: c.project_id,
        label: c.goal.slice(0, 80),
        status: c.status,
        createdAt: c.created_at,
        to: '/campaigns/$campaignId',
        params: { campaignId: c.id },
      })),
      ...forecastItems.map<ActivityRow>((f) => ({
        id: `fc-${f.id}`,
        kind: 'forecast',
        projectId: f.project_id,
        label: `${f.horizon_days}-day forecast`,
        status: f.status,
        createdAt: f.created_at,
        to: '/forecasts/$forecastId',
        params: { forecastId: f.id },
      })),
      ...competitorRunItems.map<ActivityRow>((c) => ({
        id: `cr-${c.id}`,
        kind: 'competitor',
        projectId: c.project_id,
        label: `Competitor scan · ${c.competitor_ids.length} domain${c.competitor_ids.length === 1 ? '' : 's'}`,
        status: c.status,
        createdAt: c.created_at,
        to: '/competitors/$runId',
        params: { runId: c.id },
      })),
    ];
    return rows.sort((a, b) =>
      (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
  }, [
    auditItems,
    briefItems,
    keywordItems,
    reportItems,
    campaignItems,
    forecastItems,
    competitorRunItems,
  ]);

  const activeNow = useMemo(
    () => activity.filter((r) => isInProgress(r.status)).slice(0, 6),
    [activity],
  );
  const recentlyDone = useMemo(
    () => activity.filter((r) => !isInProgress(r.status)).slice(0, 8),
    [activity],
  );

  /* ---- Per-project health snapshot ---- */
  const projectSnapshots = useMemo(() => {
    return projectItems.map((p) => buildProjectSnapshot(p, activity));
  }, [projectItems, activity]);

  /* ---- "Needs attention" — derived alerts ---- */
  const alerts = useMemo(() => {
    return buildAlerts(projectSnapshots, campaignItems);
  }, [projectSnapshots, campaignItems]);

  const greeting = user?.display_name ?? user?.email?.split('@')[0] ?? 'there';

  return (
    <Page>
      <PageHeader
        title={`Welcome back, ${greeting}`}
        description="Operations at a glance — what needs attention, what's running, and where each project stands."
      />

      {/* System pulse — 4 actionable counts */}
      <motion.div
        variants={fadeUp}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      >
        <StatTile
          icon={FolderKanban}
          label="Active projects"
          value={projectItems.length}
          hint={projectItems.length === 0 ? 'Create one to begin' : undefined}
        />
        <StatTile
          icon={Activity}
          label="Running now"
          value={totals.inProgress}
          tone={totals.inProgress > 0 ? 'info' : 'muted'}
          hint={totals.inProgress > 0 ? 'across all services' : 'all idle'}
        />
        <StatTile
          icon={AlertTriangle}
          label="Need attention"
          value={alerts.length}
          tone={alerts.length > 0 ? 'warning' : 'muted'}
          hint={alerts.length > 0 ? 'see list below' : 'nothing flagged'}
        />
        <StatTile
          icon={CheckCircle2}
          label="Done · last 7d"
          value={completedLast7}
          tone={completedLast7 > 0 ? 'success' : 'muted'}
          hint="audits + briefs + reports"
        />
      </motion.div>

      {/* Needs attention */}
      {alerts.length > 0 ? (
        <motion.div variants={fadeUp} className="space-y-2">
          {alerts.slice(0, 4).map((alert) => (
            <Callout
              key={alert.id}
              tone={alert.tone}
              title={alert.title}
              action={
                alert.to ? (
                  <Button
                    asChild
                    size="sm"
                    variant={alert.tone === 'danger' ? 'default' : 'outline'}
                  >
                    <Link
                      to={alert.to}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      params={alert.params as any}
                      hash={alert.hash}
                    >
                      {alert.actionLabel}
                    </Link>
                  </Button>
                ) : undefined
              }
            >
              {alert.description}
            </Callout>
          ))}
          {alerts.length > 4 ? (
            <div className="px-1 text-xs text-muted-foreground">
              + {alerts.length - 4} more…
            </div>
          ) : null}
        </motion.div>
      ) : null}

      {/* Loading state for first paint */}
      {isLoading && projectItems.length === 0 ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : projectItems.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="Create your first project"
          description="Each project represents one site. Once added, you can run audits, generate briefs, track competitors and more."
          action={
            <Button asChild>
              <Link to="/projects">Go to Projects</Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* Two-column: projects grid + activity panels */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Projects health */}
            <motion.div variants={fadeUp} className="lg:col-span-2">
              <Section padded={false}>
                <div className="flex items-center justify-between gap-3 border-b border-border/70 p-4">
                  <div>
                    <h2 className="text-base font-semibold leading-tight">
                      Project health
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Latest audit, integrations, and live activity for each project.
                    </p>
                  </div>
                  <Link
                    to="/projects"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    All projects <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <ul className="divide-y divide-border/60">
                  {projectSnapshots.slice(0, 6).map((snap) => (
                    <li key={snap.project.id}>
                      <ProjectSnapshotRow snap={snap} />
                    </li>
                  ))}
                </ul>
              </Section>
            </motion.div>

            {/* Active right now */}
            <motion.div variants={fadeUp}>
              <Section>
                <SectionHeader>
                  <div>
                    <SectionTitle className="flex items-center gap-2">
                      <CircleDot
                        className={cn(
                          'h-4 w-4',
                          activeNow.length > 0
                            ? 'text-info animate-pulse'
                            : 'text-muted-foreground',
                        )}
                      />
                      Running now
                    </SectionTitle>
                    <SectionDescription>
                      {activeNow.length > 0
                        ? `${activeNow.length} operation${activeNow.length === 1 ? '' : 's'} in progress.`
                        : 'No operations in progress.'}
                    </SectionDescription>
                  </div>
                </SectionHeader>
                {activeNow.length === 0 ? (
                  <EmptyState
                    icon={CircleDot}
                    title="All caught up"
                    description="Nothing's running right now. Kick off an audit or a campaign to see live updates here."
                    variant="inline"
                  />
                ) : (
                  <ul className="space-y-2">
                    {activeNow.map((row) => (
                      <ActivityRowItem
                        key={row.id}
                        row={row}
                        projectsById={lookup(projectItems)}
                      />
                    ))}
                  </ul>
                )}
              </Section>
            </motion.div>
          </div>

          {/* Recent completed activity */}
          <motion.div variants={fadeUp}>
            <Section>
              <SectionHeader>
                <div>
                  <SectionTitle className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    Recently completed
                  </SectionTitle>
                  <SectionDescription>
                    Last 8 finished events across audits, briefs, keywords,
                    reports, campaigns, forecasts, and competitors.
                  </SectionDescription>
                </div>
              </SectionHeader>
              {recentlyDone.length === 0 ? (
                <EmptyState
                  icon={Clock}
                  title="No completed work yet"
                  description="Once your runs finish, they'll show up here with a direct link to the result."
                  variant="inline"
                />
              ) : (
                <ul className="divide-y divide-border/60">
                  {recentlyDone.map((row) => (
                    <CompletedRow
                      key={row.id}
                      row={row}
                      projectsById={lookup(projectItems)}
                    />
                  ))}
                </ul>
              )}
            </Section>
          </motion.div>
        </>
      )}
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Project snapshot                                                    */
/* ------------------------------------------------------------------ */

interface ProjectSnapshot {
  project: Project;
  latestAudit: Audit | null;
  /** Across ALL services for this project. */
  inProgress: number;
  /** Counted across this project's items in last 7 days. */
  failedRecent: number;
  /** Most recent activity timestamp across all services. */
  lastActivityAt: string | null;
}

function buildProjectSnapshot(
  project: Project,
  allActivity: ActivityRow[],
): ProjectSnapshot {
  const mine = allActivity.filter((r) => r.projectId === project.id);
  // We don't have access to the audit data here, only the activity rows.
  // Reconstruct latest audit-shaped info from the activity row instead.
  const latestAuditRow = mine
    .filter((r) => r.kind === 'audit')
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
  const cutoff = Date.now() - 7 * 86400_000;
  const failedRecent = mine.filter(
    (r) =>
      isFailed(r.status) && new Date(r.createdAt).getTime() >= cutoff,
  ).length;
  const inProgress = mine.filter((r) => isInProgress(r.status)).length;
  const lastActivityAt = mine[0]?.createdAt ?? null;
  // We synthesize a minimal Audit-like object from the activity row's label.
  // The actual health score isn't available here, but for the dashboard we
  // only need status + recency; the Project page is the source of truth for
  // the full audit detail. So pass null and let the row render accordingly.
  return {
    project,
    latestAudit: latestAuditRow
      ? ({
          status: latestAuditRow.status,
          created_at: latestAuditRow.createdAt,
          // Parse the score out of "Audit · score NN" if present.
          health_score: parseScoreFromLabel(latestAuditRow.label),
        } as unknown as Audit)
      : null,
    inProgress,
    failedRecent,
    lastActivityAt,
  };
}

function parseScoreFromLabel(label: string): number | null {
  const m = label.match(/score (\d+)/);
  return m ? Number(m[1]) : null;
}

function ProjectSnapshotRow({ snap }: { snap: ProjectSnapshot }): JSX.Element {
  const p = snap.project;
  const host = hostname(p.domain);
  const favicon = `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  const latest = snap.latestAudit;
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: p.id }}
      className="group flex items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
    >
      <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/70 bg-muted/50">
        {latest && latest.health_score !== null ? (
          <div className="flex flex-col items-center leading-none">
            <span
              className={cn(
                'text-base font-semibold tabular',
                healthScoreTone(latest.health_score),
              )}
            >
              {latest.health_score}
            </span>
            <span className="text-[8px] uppercase tracking-wider text-muted-foreground">
              score
            </span>
          </div>
        ) : (
          <img
            src={favicon}
            alt=""
            width={24}
            height={24}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground group-hover:text-primary">
            {p.name}
          </span>
          {latest ? <StatusPill status={latest.status} /> : null}
        </div>
        <div className="truncate text-xs text-muted-foreground">{host}</div>
      </div>
      <div className="hidden items-center gap-3 sm:flex">
        {snap.inProgress > 0 ? (
          <Badge variant="info" className="font-mono">
            <CircleDot className="mr-1 h-3 w-3 animate-pulse" />
            {snap.inProgress} running
          </Badge>
        ) : null}
        {snap.failedRecent > 0 ? (
          <Badge variant="destructive" className="font-mono">
            {snap.failedRecent} failed
          </Badge>
        ) : null}
        <span className="text-xs text-muted-foreground tabular">
          {relTime(snap.lastActivityAt)}
        </span>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Activity row primitives                                             */
/* ------------------------------------------------------------------ */

function lookup(projects: Project[]): Map<string, Project> {
  const m = new Map<string, Project>();
  for (const p of projects) m.set(p.id, p);
  return m;
}

function ActivityRowItem({
  row,
  projectsById,
}: {
  row: ActivityRow;
  projectsById: Map<string, Project>;
}): JSX.Element {
  const Icon = KIND_META[row.kind].icon;
  const project = projectsById.get(row.projectId);
  return (
    <li>
      <Link
        to={row.to}
        params={row.params}
        className="group flex items-center gap-3 rounded-md border border-border/70 bg-card p-2.5 transition-colors hover:border-primary/40 hover:bg-accent/30"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium group-hover:text-primary">
            {row.label}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {project?.name ?? row.projectId.slice(0, 8)} ·{' '}
            {KIND_META[row.kind].label}
          </div>
        </div>
        <StatusPill status={row.status} />
      </Link>
    </li>
  );
}

function CompletedRow({
  row,
  projectsById,
}: {
  row: ActivityRow;
  projectsById: Map<string, Project>;
}): JSX.Element {
  const Icon = KIND_META[row.kind].icon;
  const project = projectsById.get(row.projectId);
  return (
    <li>
      <Link
        to={row.to}
        params={row.params}
        className="group flex items-center gap-3 py-3 transition-colors first:pt-0 last:pb-0 hover:text-primary"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{row.label}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {KIND_META[row.kind].label} ·{' '}
            {project?.name ?? row.projectId.slice(0, 8)}
          </div>
        </div>
        <StatusPill status={row.status} />
        <span className="hidden w-20 shrink-0 text-right text-xs text-muted-foreground tabular sm:inline">
          {relTime(row.createdAt)}
        </span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </Link>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Alerts derivation                                                   */
/* ------------------------------------------------------------------ */

interface DashAlert {
  id: string;
  tone: 'info' | 'warning' | 'danger';
  title: string;
  description: string;
  to?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  hash?: string;
  actionLabel?: string;
}

function buildAlerts(
  snapshots: ProjectSnapshot[],
  campaigns: Campaign[],
): DashAlert[] {
  const out: DashAlert[] = [];

  // Failed runs in the last 7 days
  const failedByProject = snapshots
    .filter((s) => s.failedRecent > 0)
    .sort((a, b) => b.failedRecent - a.failedRecent);
  for (const s of failedByProject) {
    out.push({
      id: `failed-${s.project.id}`,
      tone: 'danger',
      title: `${s.failedRecent} failed run${s.failedRecent === 1 ? '' : 's'} in ${s.project.name}`,
      description: 'Check the failure reason and re-run, or adjust the inputs.',
      to: '/projects/$projectId',
      params: { projectId: s.project.id },
      actionLabel: 'Open project',
    });
  }

  // Pending approvals (campaigns needing review)
  const pending = campaigns.filter((c) => isNeedsReview(c.status));
  for (const c of pending) {
    out.push({
      id: `pending-${c.id}`,
      tone: 'warning',
      title: 'Campaign awaiting approval',
      description: c.goal.slice(0, 120),
      to: '/campaigns/$campaignId',
      params: { campaignId: c.id },
      actionLabel: 'Review',
    });
  }

  // Projects with no audit in the last 30 days
  const cutoff = Date.now() - 30 * 86400_000;
  const stale = snapshots.filter((s) => {
    if (!s.latestAudit) return true;
    const t = new Date(s.latestAudit.created_at).getTime();
    return t < cutoff;
  });
  if (stale.length > 0 && stale.length <= 3) {
    for (const s of stale) {
      out.push({
        id: `stale-${s.project.id}`,
        tone: 'info',
        title: `${s.project.name} hasn't been audited recently`,
        description: s.latestAudit
          ? `Last audit ${relTime(s.latestAudit.created_at)}. Re-run to catch regressions.`
          : 'No audit yet — run one to establish a baseline.',
        to: '/projects/$projectId',
        params: { projectId: s.project.id },
        hash: 'audits',
        actionLabel: 'Run audit',
      });
    }
  } else if (stale.length > 3) {
    out.push({
      id: 'stale-many',
      tone: 'info',
      title: `${stale.length} projects haven't been audited in 30+ days`,
      description: 'Schedule fresh audits to keep health scores current.',
      to: '/projects',
      actionLabel: 'Browse projects',
    });
  }

  return out;
}

function healthScoreTone(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-foreground';
  if (score >= 40) return 'text-warning';
  return 'text-destructive';
}
